import type { Task } from '@/tasks/task';
import { kindColor } from './KindLabel';

/** 「すべて」タブを表す値。種別名と衝突しないよう空文字を使う */
/** @graph-connects none */
export const ALL_KINDS = '';

/**
 * KindTabs の props。
 */
export interface KindTabsProps {
  /** 件数を数える対象のタスク（表示範囲のもの） */
  tasks: readonly Task[];
  /** 選択中の種別。`ALL_KINDS` なら全件 */
  selected: string;
  /** タブを選んだときに呼ばれる */
  onSelect: (kind: string) => void;
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 種別ごとの絞り込みタブ。表示範囲に出てくる種別だけを件数つきで並べる
 * @graph-connects none
 */
export function KindTabs({ tasks, selected, onSelect }: KindTabsProps) {
  // 表示範囲に存在する種別だけを出す。空のタブを並べても選ぶ意味がない
  const counts = new Map<string, number>();
  for (const task of tasks) {
    counts.set(task.kind, (counts.get(task.kind) ?? 0) + 1);
  }
  // 件数のある種別だけをタブにする。並びは名前順
  const kinds = [...counts.entries()].sort(([a], [b]) => a.localeCompare(b, 'ja'));

  const tabClass = (active: boolean): string =>
    active
      ? 'relative -mb-px rounded-t border border-b-0 border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] px-3 py-1.5 text-sm font-medium text-[var(--color-text-primary)]'
      : 'rounded-t border border-transparent px-3 py-1.5 text-sm text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)] hover:text-[var(--color-text-primary)]';

  return (
    <div
      role="tablist"
      aria-label="種別の絞り込み"
      className="flex flex-wrap items-end gap-1 border-b border-[var(--color-border-subtle)]"
    >
      <button
        type="button"
        role="tab"
        aria-selected={selected === ALL_KINDS}
        onClick={() => {
          onSelect(ALL_KINDS);
        }}
        className={tabClass(selected === ALL_KINDS)}
      >
        すべて
        <span className="ml-1.5 text-xs tabular-nums text-[var(--color-text-muted)]">
          {tasks.length}
        </span>
      </button>

      {kinds.map(([kind, count]) => (
        <button
          key={kind}
          type="button"
          role="tab"
          aria-selected={selected === kind}
          onClick={() => {
            onSelect(kind);
          }}
          className={tabClass(selected === kind)}
        >
          <span
            aria-hidden="true"
            className="mr-1.5 inline-block h-2 w-2 rounded-full align-middle"
            style={{ backgroundColor: kindColor(kind) }}
          />
          {kind}
          <span className="ml-1.5 text-xs tabular-nums text-[var(--color-text-muted)]">
            {count}
          </span>
        </button>
      ))}
    </div>
  );
}
